/**
 * Floating "No slots available" banner over the calendar — mirrors the
 * legacy KInterviewScheduler SchInterviewerFieldNoCache popover.
 *
 * Shown when the user has picked interviewers (± a meeting room) but
 * the gap-finder produced zero bookable slots for the visible week.
 * Dismissable; reappears when the conditions re-trigger (e.g. roster
 * change, week nav).
 */
import { useState } from 'react';
import { Box, CloseButton, Flex } from '@chakra-ui/react';
import { FaceFrown } from '@kula-ai/icons-core';
import { KText } from '@/ui-library/components/text';

export interface NoSlotsAvailablePopoverProps {
  /** Fires the popover — true when data has loaded and zero slots
   *  were produced by the gap-finder. */
  visible: boolean;
  /**
   * Tweaks the copy:
   *   - 'week'      — "No slots available this week"
   *   - 'duration'  — "No slots available" (visible-week with current duration)
   *   - 'find-time' — "Selected interviewers not available". Fired
   *                   when the user picked a Find Time mode and the
   *                   walker found zero days in the prefetch horizon.
   */
  reason?: 'week' | 'duration' | 'find-time';
}

/**
 * Parent should pass a fresh `key` (week-start ISO or roster hash) so
 * React unmounts the component when the context changes — that resets
 * the internal `dismissed` state without a synchronous setState inside
 * an effect (see react-hooks/set-state-in-effect).
 */
export const NoSlotsAvailablePopover = ({
  visible,
  reason = 'week',
}: NoSlotsAvailablePopoverProps) => {
  const [dismissed, setDismissed] = useState(false);
  if (!visible || dismissed) return null;

  let headline: string;
  let body: string;
  if (reason === 'find-time') {
    headline = 'Selected interviewers not available';
    body =
      'The selected interviewers are not available for this option. Replace interviewers to find different time slots.';
  } else if (reason === 'duration') {
    headline = 'No slots available';
    body =
      'Try a different time period or replace interviewers to find open time slots';
  } else {
    headline = 'No slots available this week';
    body =
      'Try a different week or replace interviewers to find open time slots';
  }

  return (
    <Box
      position="absolute"
      top="50%"
      right="24px"
      transform="translateY(-50%)"
      zIndex={30}
      bg="white"
      boxShadow="0px 4px 14px rgba(0, 0, 0, 0.14)"
      borderRadius="6px"
      p="12px 14px"
      maxW="340px"
    >
      <Flex flexDir="column" gap="6px">
        <Flex align="center" gap="2">
          <FaceFrown size={18} className="text-secondary-700" />
          <KText variant="heading_4" fontSize="sm">
            {headline}
          </KText>
          <CloseButton
            size="sm"
            ml="auto"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
          />
        </Flex>
        <KText fontSize="xs" color="secondary-600">
          {body}
        </KText>
      </Flex>
    </Box>
  );
};
